import { Entity } from '@api/api/entity-schema';
import { ThemeSchemaResponse } from '@api/api/theme-overview';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { useGraphRelations } from '@terminal/react-query-hooks/graph/useGraphRelations';
import { useCatalystInsightsList } from '@terminal/react-query-hooks/insights/useCatalystInsights';
import { useEarningsInsightsFilter } from '@terminal/react-query-hooks/insights/useEarningsInsightsFilter';
import { parseEarningsInsights } from '@terminal/react-query-hooks/insights/useEarningsInsightsFilter.utils';
import clsx from 'clsx';
import { startOfDay, subWeeks } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetLinkToCompaniesSubtab } from '../../overview/top-companies-section/utils';
import styles from './MacroFinancialTopCompaniesSection.module.scss';
import { TopCompaniesTable } from './top-companies-table/TopCompaniesTable';

const TOP_FIVE_COMPANIES = 5;

export const MacroFinancialTopCompaniesSection = ({
  theme,
  isCompact = false,
}: {
  theme: ThemeSchemaResponse;
  isCompact?: boolean;
}) => {
  const { t } = useTranslation(['widget']);
  const { data, isLoading, isError, error } = useGraphRelations({
    node: theme.id,
    nodeType: 'theme',
    relationKind: 'theme-company',
    maxRank: TOP_FIVE_COMPANIES,
    direction: 'outbound',
  });

  const topFiveCompanyEdges =
    data?.edges
      .filter(
        edge =>
          edge.attributes.type === 'theme-company-financial' ||
          edge.attributes.type === 'theme-company-macro'
      )
      .slice(0, TOP_FIVE_COMPANIES) ?? [];

  const tags = topFiveCompanyEdges
    .map(edge => edge.target.split('/')[1])
    .filter((id): id is string => id != null);

  const {
    data: dataEntities,
    isLoading: isEntitiesLoading,
    isError: isEntitiesError,
  } = useEntities({
    entityTags: tags,
    enabled: tags.length > 0,
  });

  const fromDate = useMemo(() => startOfDay(subWeeks(new Date(), 1)), []);
  const canLoadCompanyDetails =
    !isCompact && !isLoading && !isError && tags.length > 0;

  const {
    data: catalystInsights,
    isLoading: isCatalystInsightsLoading,
    isError: isCatalystInsightsError,
  } = useCatalystInsightsList(
    {
      entities: tags,
      categories: ['company'],
      from: fromDate.toISOString(),
    },
    canLoadCompanyDetails
  );

  const { data: earningsInsightsData = [] } = useEarningsInsightsFilter({
    body: { entities: tags, from: fromDate.toISOString() },
    isEnabled: canLoadCompanyDetails,
  });

  const linkToCompaniesSubTab = useGetLinkToCompaniesSubtab(theme.id);

  const isSectionLoading =
    isLoading || isEntitiesLoading || (!isCompact && isCatalystInsightsLoading);

  if (isSectionLoading) {
    return (
      <SkeletonLoader
        className={clsx(styles.skeletonWrapper, {
          [styles.compactSkeleton]: isCompact,
        })}
        areas={[
          {
            styles: {
              width: '100%',
              height: '100%',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (isError || isEntitiesError || (!isCompact && isCatalystInsightsError)) {
    throw new Error(
      error?.message || 'TopCompaniesSection: failed to fetch entities data'
    );
  }

  const sanitizedCompanyEdges = topFiveCompanyEdges.filter(edge =>
    dataEntities.some(entity => entity.tag === edge.target.split('/')[1])
  );

  const dataWithInsights = sanitizedCompanyEdges.map(edge => {
    const tag = edge.target.split('/')[1];
    const entity = dataEntities.find(item => item.tag === tag) as Entity;
    const companyEarnings = earningsInsightsData.filter(
      e => e.entity_tag === tag
    );

    return {
      rank: edge.attributes.rank,
      exposure_direction: edge.attributes.exposure_direction,
      entity,
      insights:
        catalystInsights?.filter(insight => insight.identifier === tag) ?? [],
      earningsInsight: parseEarningsInsights(companyEarnings),
    };
  });

  if (!dataWithInsights.length) {
    return (
      <div className={styles.root}>
        <ErrorMessageWrapper variant="empty" showBorder={false} />
      </div>
    );
  }

  return (
    <div
      className={clsx(styles.root, {
        [styles.compact]: isCompact,
      })}
    >
      <div className={styles.titleWrapper}>
        <div className={clsx({ [styles.sectionTitle]: !isCompact })}>
          <SectionTitle
            title={t('widget:themeOverview.tabs.overview.topCompanies', {
              count: dataWithInsights.length,
            })}
            linkTo={isCompact ? undefined : linkToCompaniesSubTab}
            variant={isCompact ? 'small' : 'default'}
          />
        </div>
        <div className={styles.subtitleLabel}>
          {t('widget:themeOverview.tabs.overview.bySignificanceRank')}
        </div>
      </div>
      <TopCompaniesTable
        data={dataWithInsights}
        theme={theme}
        isCompact={isCompact}
      />
    </div>
  );
};
